import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";

// Lightweight worldwide address autocomplete via Photon (OpenStreetMap) — free, no API key.
export const AddressAutocomplete = ({ value, onType, onSelect, placeholder, testId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);
  const skipNext = useRef(false);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    if (!value || value.trim().length < 4) { setSuggestions([]); setOpen(false); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(value)}&limit=6`);
        const data = await res.json();
        const feats = (data.features || []).filter((f) => f.properties && (f.properties.street || f.properties.name));
        setSuggestions(feats);
        setOpen(feats.length > 0);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [value]);

  const label = (p) => {
    const line1 = [p.housenumber, p.street || p.name].filter(Boolean).join(" ");
    const line2 = [p.city || p.county, p.state, p.postcode, p.country].filter(Boolean).join(", ");
    return [line1, line2].filter(Boolean).join(" · ");
  };

  const choose = (f) => {
    const p = f.properties;
    const street1 = [p.housenumber, p.street || p.name].filter(Boolean).join(" ");
    skipNext.current = true;
    onSelect({
      street1,
      city: p.city || p.county || p.district || "",
      state: p.state || "",
      zip: p.postcode || "",
      country: (p.countrycode || "").toUpperCase(),
    });
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative col-span-2" ref={boxRef}>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          data-testid={testId}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full border border-[#D1CCC5] rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-[#8E9A86]" />
        <MapPin className="h-4 w-4 text-[#8A8175] absolute left-2.5 top-1/2 -translate-y-1/2" />
        {loading && <Loader2 className="h-4 w-4 text-[#8A8175] absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin" />}
      </div>
      {open && (
        <ul data-testid="address-suggestions" className="absolute z-[70] mt-1 w-full bg-white border border-[#D9CBA0]/70 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {suggestions.map((f, i) => (
            <li key={i}>
              <button type="button" onClick={() => choose(f)} data-testid={`address-suggestion-${i}`}
                className="w-full text-left px-3 py-2 text-xs text-[#3A3632] hover:bg-[#F2EFEB] border-b border-[#E5DFD5]/60 last:border-0">
                {label(f.properties)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
